import React, { useState, useRef, useEffect } from "react";
import "../index.css";
import RuleLogicComponent, {
  //  StepperInput,
  GenericValueChange,
  GenericPercentChange,
  GenericHitsValue,
  GenericTeamValueChange,
  GenericTeamPercentChange,
  GenericTeamHitsValue,
  type HitsValueLineType,
} from "./RuleLogic";
import RuleOptionMultiSelect from "./RuleOptionSelect";
import {
  RULE_OPTION_SELECTORS,
  findRuleOptionSelector,
} from "./ruleOptionSelectors";
import { findRuleDescription } from "./ruleDescriptions";
import { getLineType } from "./ruleLineType";
import buttonBackground from "../assets/button_background_blue.webp";

// const inputTextStyle: React.CSSProperties = {
//   fontSize: 16,
//   color: "#FFF",
//   lineHeight: "30px",
//   whiteSpace: "pre-wrap",
// };

// Custom dropdown component for rule selection
interface CategoryOption {
  label: string;
  displayLabel: string;
  value: string;
  disabled?: boolean;
  requiredLevel?: string;
}

interface CustomRuleDropdownProps {
  value: string;
  onChange: (value: string) => void;
  // A category keyed "" renders its options without a header.
  categories: { [category: string]: CategoryOption[] };
  placeholder?: string;
  disabled?: boolean;
}

const CustomRuleDropdown: React.FC<CustomRuleDropdownProps> = ({
  value,
  onChange,
  categories,
  placeholder = "Select Rule Type",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get display label for selected value
  const getDisplayLabel = (): string => {
    if (value === "Select an option" || value === "") return placeholder;
    for (const [, options] of Object.entries(categories)) {
      const found = options.find((opt) => opt.value === value);
      if (found) return found.displayLabel;
    }
    return value;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      {/* Selected value display */}
      <div
        className="font_family_base"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          backgroundColor: disabled ? "#f3f4f6" : "#ffffff",
          border: "1px solid #d1d5db",
          borderRadius: 8,
          padding: "12px 16px",
          fontSize: 16,
          color: disabled ? "#9ca3af" : "#374151",
          width: "100%",
          boxSizing: "border-box",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{getDisplayLabel()}</span>
        <span style={{ marginLeft: 8 }}>{isOpen ? "▲" : "▼"}</span>
      </div>

      {/* Dropdown list */}
      {isOpen && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "#ffffff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            marginTop: 4,
            maxHeight: 300,
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {Object.entries(categories).map(
            ([category, options]) =>
              options.length > 0 && (
                <div key={category}>
                  {/* Category header */}
                  {category !== "" && (
                    <div
                      className="font_family_base"
                      style={{
                        padding: "8px 16px",
                        fontSize: 14,
                        fontWeight: "bold",
                        color: "#6b7280",
                        backgroundColor: "#f3f4f6",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      {category}
                    </div>
                  )}
                  {/* Category options */}
                  {options.map((item) => (
                    <div
                      key={item.value}
                      className="font_family_base"
                      onClick={() => !item.disabled && handleSelect(item.value)}
                      style={{
                        padding:
                          category !== ""
                            ? "10px 16px 10px 24px"
                            : "10px 16px",
                        fontSize: 16,
                        color: item.disabled
                          ? "#9ca3af"
                          : item.value === value
                            ? "#2563eb"
                            : "#374151",
                        backgroundColor:
                          item.value === value && !item.disabled
                            ? "#eff6ff"
                            : "transparent",
                        cursor: item.disabled ? "not-allowed" : "pointer",
                        borderBottom: "1px solid #f3f4f6",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => {
                        if (!item.disabled && item.value !== value) {
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!item.disabled && item.value !== value) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <span>{item.label}</span>
                      {item.disabled && item.requiredLevel && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#fff",
                            backgroundColor:
                              item.requiredLevel === "Premium"
                                ? "#7c3aed"
                                : "#2563eb",
                            borderRadius: 4,
                            padding: "2px 6px",
                            marginLeft: 8,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.requiredLevel}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ),
          )}
        </div>
      )}
    </div>
  );
};

// ── Subtype configuration ─────────────────────────────────────────────────────

// interface SubtypeField {
//   key: string;
//   label: string;
//   type: "number" | "direction";
// }

// interface SubtypeConfig {
//   label: string;
//   fields: SubtypeField[];
//   buildSummary: (details: Record<string, unknown>) => string;
// }

// const SUBTYPE_CONFIG: Record<number, SubtypeConfig> = {
//   1: {
//     label: "Moneyline Value Change",
//     fields: [
//       { key: "direction", label: "Direction", type: "direction" },
//       { key: "threshold", label: "Change Amount (points)", type: "number" },
//     ],
//     buildSummary: (d) =>
//       `When the moneyline ${d.direction === "up" ? "increases" : d.direction === "down" ? "decreases" : "changes"} by ${d.threshold ?? "?"} points`,
//   },
//   2: {
//     label: "Moneyline Percent Change",
//     fields: [
//       { key: "direction", label: "Direction", type: "direction" },
//       { key: "threshold", label: "Change Amount (%)", type: "number" },
//     ],
//     buildSummary: (d) =>
//       `When the moneyline ${d.direction === "up" ? "increases" : d.direction === "down" ? "decreases" : "changes"} by ${d.threshold ?? "?"}%`,
//   },
//   3: {
//     label: "Moneyline Hits Value",
//     fields: [{ key: "value", label: "Target Odds Value", type: "number" }],
//     buildSummary: (d) => `Alert when the moneyline hits ${d.value ?? "?"}`,
//   },
//   4: {
//     label: "Over/Under Value Change",
//     fields: [
//       { key: "direction", label: "Direction", type: "direction" },
//       { key: "threshold", label: "Change Amount", type: "number" },
//     ],
//     buildSummary: (d) =>
//       `When the over/under line ${d.direction === "up" ? "increases" : d.direction === "down" ? "decreases" : "changes"} by ${d.threshold ?? "?"}`,
//   },
//   5: {
//     label: "Over/Under Percent Change",
//     fields: [
//       { key: "direction", label: "Direction", type: "direction" },
//       { key: "threshold", label: "Change Amount (%)", type: "number" },
//     ],
//     buildSummary: (d) =>
//       `Alert when the over/under line ${d.direction === "up" ? "increases" : d.direction === "down" ? "decreases" : "changes"} by ${d.threshold ?? "?"}%`,
//   },
//   6: {
//     label: "Over/Under Hits Value",
//     fields: [{ key: "value", label: "Target Line Value", type: "number" }],
//     buildSummary: (d) =>
//       `Alert when the over/under line hits ${d.value ?? "?"}`,
//   },
// };

// ── Subtype 4: Over/Under Value Change — inline sentence style ────────────────

// interface Subtype4FormProps {
//   details: Record<string, unknown>;
//   onChange: (details: Record<string, unknown>) => void;
// }

// const Subtype4Form: React.FC<Subtype4FormProps> = ({ details, onChange }) => {
//   const threshold = (details["threshold"] as number) ?? 0.5;
//   const direction = (details["direction"] as string) ?? "";

//   const handleThreshold = (val: number) => {
//     onChange({ ...details, threshold: val });
//   };

//   const handleDirection = (dir: string) => {
//     onChange({ ...details, direction: dir });
//   };

//   const directionLabel =
//     direction === "up"
//       ? "increases"
//       : direction === "down"
//         ? "decreases"
//         : "___";

//   return (
//     <div style={{ marginTop: 10, marginBottom: 10 }}>
//       <div
//         style={{
//           display: "flex",
//           flexDirection: "row",
//           flexWrap: "wrap",
//           alignItems: "flex-start",
//           justifyContent: "center",
//         }}
//       >
//         <span className="font_family_base" style={inputTextStyle}>
//           When the Over/Under{" "}
//         </span>
//         <span
//           className="font_family_base"
//           style={{
//             ...inputTextStyle,
//             display: "inline-flex",
//             gap: 4,
//             alignItems: "center",
//           }}
//         >
//           <button
//             type="button"
//             className="font_family_base"
//             onClick={() => handleDirection("up")}
//             style={{
//               padding: "2px 8px",
//               borderRadius: 4,
//               border: `1px solid ${direction === "up" ? "#4a9eff" : "#555"}`,
//               background: direction === "up" ? "#1a3a5c" : "transparent",
//               color: direction === "up" ? "#4a9eff" : "#9ca3af",
//               fontSize: 13,
//               fontWeight: direction === "up" ? 700 : 400,
//               cursor: "pointer",
//               lineHeight: "22px",
//             }}
//           >
//             ↑ Up
//           </button>
//           <button
//             type="button"
//             className="font_family_base"
//             onClick={() => handleDirection("down")}
//             style={{
//               padding: "2px 8px",
//               borderRadius: 4,
//               border: `1px solid ${direction === "down" ? "#4a9eff" : "#555"}`,
//               background: direction === "down" ? "#1a3a5c" : "transparent",
//               color: direction === "down" ? "#4a9eff" : "#9ca3af",
//               fontSize: 13,
//               fontWeight: direction === "down" ? 700 : 400,
//               cursor: "pointer",
//               lineHeight: "22px",
//             }}
//           >
//             ↓ Down
//           </button>
//         </span>
//         <span className="font_family_base" style={inputTextStyle}>
//           {" "}
//           {directionLabel} by{" "}
//         </span>
//         <StepperInput
//           initialValue={threshold}
//           min={0.5}
//           max={9999}
//           step={0.5}
//           bigStep={10}
//           onValueChange={handleThreshold}
//           skipZero={false}
//           showDecimalValue={true}
//           showPercent={false}
//           useBigStep={true}
//           ordinalMap={false}
//           showPlusSign={false}
//         />
//       </div>
//     </div>
//   );
// };

// ─────────────────────────────────────────────────────────────────────────────

// interface SubtypeParamsFormProps {
//   subtype: number;
//   details: Record<string, unknown>;
//   onChange: (details: Record<string, unknown>) => void;
// }

// const SubtypeParamsForm: React.FC<SubtypeParamsFormProps> = ({
//   subtype,
//   details,
//   onChange,
// }) => {
//   if (subtype === 4) {
//     return <Subtype4Form details={details} onChange={onChange} />;
//   }

//   const config = SUBTYPE_CONFIG[subtype];
//   if (!config) return null;

//   const update = (key: string, value: unknown) => {
//     onChange({ ...details, [key]: value });
//   };

//   return (
//     <div
//       style={{
//         padding: "0 16px",
//         maxWidth: 420,
//         margin: "0 auto",
//         marginTop: 8,
//       }}
//     >
//       <p
//         className="font_family_base"
//         style={{
//           color: "#6b7280",
//           fontSize: 13,
//           marginBottom: 16,
//           textAlign: "center",
//           fontStyle: "italic",
//         }}
//       >
//         {config.buildSummary(details)}
//       </p>
//       {config.fields.map((field) => (
//         <div key={field.key} style={{ marginBottom: 14 }}>
//           <label
//             className="font_family_base"
//             style={{
//               fontSize: 13,
//               color: "#6b7280",
//               display: "block",
//               marginBottom: 6,
//             }}
//           >
//             {field.label}
//           </label>
//           {field.type === "direction" ? (
//             <div style={{ display: "flex", gap: 8 }}>
//               {(["up", "down"] as const).map((dir) => (
//                 <button
//                   key={dir}
//                   type="button"
//                   className="font_family_base"
//                   onClick={() => update(field.key, dir)}
//                   style={{
//                     flex: 1,
//                     padding: "9px 0",
//                     borderRadius: 6,
//                     border: `2px solid ${details[field.key] === dir ? "#2563eb" : "#d1d5db"}`,
//                     background:
//                       details[field.key] === dir ? "#eff6ff" : "transparent",
//                     color: details[field.key] === dir ? "#2563eb" : "#6b7280",
//                     fontSize: 14,
//                     fontWeight: details[field.key] === dir ? 700 : 400,
//                     cursor: "pointer",
//                   }}
//                 >
//                   {dir === "up" ? "↑ Up" : "↓ Down"}
//                 </button>
//               ))}
//             </div>
//           ) : (
//             <input
//               type="number"
//               className="font_family_base"
//               value={
//                 details[field.key] !== undefined
//                   ? String(details[field.key])
//                   : ""
//               }
//               onChange={(e) =>
//                 update(
//                   field.key,
//                   e.target.value === ""
//                     ? undefined
//                     : parseFloat(e.target.value),
//                 )
//               }
//               style={{
//                 backgroundColor: "white",
//                 border: "1px solid #d1d5db",
//                 padding: "10px 12px",
//                 borderRadius: 8,
//                 fontSize: 15,
//                 width: "100%",
//                 boxSizing: "border-box",
//                 color: "#374151",
//               }}
//             />
//           )}
//         </div>
//       ))}
//     </div>
//   );
// };

// ─────────────────────────────────────────────────────────────────────────────

interface ValidationError {
  title: string;
  message: string;
}

// Type definitions
interface RuleDetails {
  [key: string]: unknown;
}

interface Rule {
  [key: string]: unknown;
}

// interface RuleOption {
//   text: string;
//   defaults: RuleDetails;
//   example: string;
//   min_level: string;
//   min_period_count: number;
// }

// interface RuleOptionsMap {
//   [key: string]: RuleOption;
// }

interface WatcherFormRuleProps {
  formState: number;
  setFormState: (state: number) => void;
  rule: Rule;
  setRule: (rules: Rule) => void;
  gamePartName: string;
  gamePartCount: number;
  ruleTypeId?: number;
  sportId?: number;
  ruleDefinitions?: Record<string, unknown>;
  ruleGroups?: Record<string, unknown>;
  inEditMode?: boolean;
}

const WatcherFormRule: React.FC<WatcherFormRuleProps> = ({
  formState,
  setFormState,
  rule,
  setRule,
  gamePartName,
  gamePartCount,
  ruleTypeId,
  sportId,
  ruleDefinitions,
  ruleGroups,
  inEditMode = false,
}) => {
  const innerRule =
    Object.keys(rule).length === 0
      ? {
          type: "Select an option",
          details: {},
        }
      : rule;
  const [ruleDetails, setRuleDetails] = useState(innerRule["details"]);
  const [ruleType, setRuleType] = useState(innerRule["type"] as string);
  const [showDetails, setShowDetails] = useState(
    ruleType !== "" && ruleType !== "Select an option",
  );
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);
  const [showExample, setShowExample] = useState(false);
  // Saved multi-select choices (see RuleOptionSelect), seeded from the rule
  // being edited.
  const [optionSelections, setOptionSelections] = useState<
    Record<string, string[]>
  >(() => {
    const saved: Record<string, string[]> = {};
    const details = (innerRule["details"] ?? {}) as Record<string, unknown>;
    RULE_OPTION_SELECTORS.forEach(({ detailKey }) => {
      if (Array.isArray(details[detailKey]))
        saved[detailKey] = details[detailKey] as string[];
    });
    return saved;
  });
  // Yes/No side for Yes/No markets (rule definitions flagged `yes_no` by the
  // backend, e.g. Both Teams Score), saved as details.side. Kept outside
  // ruleDetails for the same reason as optionSelections.
  const [yesNoSide, setYesNoSide] = useState<"yes" | "no">(() =>
    (innerRule["details"] as Record<string, unknown> | undefined)?.["side"] ===
    "no"
      ? "no"
      : "yes",
  );
  // Rule group chosen in the first dropdown; empty until picked (see effectiveGroup).
  const [selectedGroup, setSelectedGroup] = useState("");
  //const ruleOptions: RuleOptionsMap = RuleOptions();

  // Determine user's effective membership level
  const getUserLevel = (): string => {
    const stateId = localStorage.getItem("state_id");
    if (stateId === "7" || stateId === "8") return "Premium";
    const subName = (
      localStorage.getItem("subscription_name") || ""
    ).toLowerCase();
    if (subName.includes("premium")) return "Premium";
    if (subName.includes("pro")) return "Pro";
    // Fallback: numeric access_level from backend
    const accessLevel = parseInt(
      localStorage.getItem("access_level") || "0",
      10,
    );
    if (accessLevel >= 3) return "Premium";
    if (accessLevel >= 2) return "Pro";
    return "Basic";
  };

  const levelRank: Record<string, number> = { Basic: 1, Pro: 2, Premium: 3 };
  const userLevel = getUserLevel();
  const isAccessible = (minLevel: string): boolean =>
    (levelRank[userLevel] ?? 1) >= (levelRank[minLevel] ?? 1);

  // Filter rule_definitions entries by ruleTypeId only
  const filteredRuleDefEntries: Array<[string, Record<string, unknown>]> = [];
  if (ruleDefinitions && Object.keys(ruleDefinitions).length > 0) {
    Object.entries(ruleDefinitions).forEach(([key, rawEntry]) => {
      const entry = rawEntry as Record<string, unknown>;
      const defTypeId = entry["rule_type_id"];
      if (defTypeId !== ruleTypeId) return;
      const defSportIds = entry["sport_ids"];
      if (defSportIds !== null && defSportIds !== undefined) {
        const ids = defSportIds as number[];
        if (ids.length > 0 && !ids.includes(sportId as number)) return;
      }
      filteredRuleDefEntries.push([key, entry]);
    });
  }
  const useRuleDefinitions = filteredRuleDefEntries.length > 0;

  const hasLockedRules = filteredRuleDefEntries.some(
    ([, entry]) => !isAccessible((entry["min_level"] as string) || "Basic"),
  );

  // Organize rule options into categories with display labels
  const ruleCategories: { [category: string]: CategoryOption[] } = {};
  const categoryByKey: Record<string, string> = {};

  // MLS Game Odds rules: only Moneyline is supported for now — the other
  // groups (Spread, Over/Under, etc.) don't have reliable data for MLS yet.
  const MLS_SPORT_ID = 7;
  const GAME_ODDS_RULE_TYPE_ID = 1;
  const onlyShowMoneyline =
    !inEditMode &&
    sportId === MLS_SPORT_ID &&
    ruleTypeId === GAME_ODDS_RULE_TYPE_ID;

  // MLB/MLS/NHL/NFL/NCAAF game props: the "Team"/"Teams" rule group only holds
  // Team Over/Under, which the Over/Under group already covers — hide the whole group.
  const HIDE_TEAM_GROUP_SPORT_IDS = [
    3, // NHL
    4, // MLB
    5, // NFL
    6, // NCAAF
    MLS_SPORT_ID,
  ];
  const GAME_PROPS_RULE_TYPE_ID = 4;
  const hideTeamGroup =
    !inEditMode &&
    HIDE_TEAM_GROUP_SPORT_IDS.includes(sportId as number) &&
    ruleTypeId === GAME_PROPS_RULE_TYPE_ID;

  if (useRuleDefinitions) {
    filteredRuleDefEntries.forEach(([key, entry]) => {
      const defMinLevel = (entry["min_level"] as string) || "Basic";
      const accessible = isAccessible(defMinLevel);
      const groupId = entry["rule_group_id"];
      let category = "Rules";
      if (groupId != null) {
        const groupEntry = ruleGroups?.[String(groupId)];
        if (typeof groupEntry === "string") category = groupEntry;
        else if (groupEntry && typeof groupEntry === "object")
          category =
            ((groupEntry as Record<string, unknown>)[
              "description"
            ] as string) || String(groupId);
        else category = String(groupId);
      }
      if (onlyShowMoneyline && !category.toLowerCase().includes("moneyline")) {
        return;
      }
      if (hideTeamGroup && (category === "Team" || category === "Teams")) return;
      const LABEL_OVERRIDES: Record<string, string> = {
        "First Half Outcome": "First Half Moneyline",
      };
      const rawLabel = (entry["description"] as string) || key;
      const baseLabel = LABEL_OVERRIDES[rawLabel] ?? rawLabel;
      const subtypeRaw = entry["rule_subtype_id"];
      const subtypeNum = subtypeRaw != null ? Number(subtypeRaw) : null;
      const subtypeSuffix =
        subtypeNum === 1 || subtypeNum === 4
          ? " Value Change"
          : subtypeNum === 2 || subtypeNum === 5
            ? " Percent Change"
            : subtypeNum === 3 || subtypeNum === 6
              ? " Hits Value"
              : "";
      const fullLabel = baseLabel + subtypeSuffix;
      categoryByKey[key] = category;
      if (!ruleCategories[category]) ruleCategories[category] = [];
      ruleCategories[category].push({
        label: fullLabel,
        displayLabel: fullLabel,
        value: key,
        disabled: !accessible,
        requiredLevel: !accessible ? defMinLevel : undefined,
      });
    });
  } else {
    // ruleCategories["Over/Under"] = [];
    // ruleCategories["Spread"] = [];
    // ruleCategories["Moneyline"] = [];
    // ruleCategories["Combination"] = [];
    // Object.keys(ruleOptions).forEach((key) => {
    //   const opt = ruleOptions[key];
    //   if ((opt.min_period_count ?? 0) > gamePartCount) return;
    //   const shortLabel = opt.text;
    //   const accessible = isAccessible(opt.min_level);
    //   const entry: CategoryOption = {
    //     label: shortLabel,
    //     displayLabel: `(${key.split(" ")[0]}) ${shortLabel}`,
    //     value: key,
    //     disabled: !accessible,
    //     requiredLevel: !accessible ? opt.min_level : undefined,
    //   };
    //   if (key.toLowerCase().startsWith("over/under")) {
    //     entry.displayLabel = `(Over/Under) ${shortLabel}`;
    //     ruleCategories["Over/Under"].push(entry);
    //   } else if (key.toLowerCase().startsWith("spread")) {
    //     entry.displayLabel = `(Spread) ${shortLabel}`;
    //     ruleCategories["Spread"].push(entry);
    //   } else if (key.toLowerCase().startsWith("moneyline")) {
    //     entry.displayLabel = `(Moneyline) ${shortLabel}`;
    //     ruleCategories["Moneyline"].push(entry);
    //   } else if (key.toLowerCase().startsWith("combination")) {
    //     entry.displayLabel = `(Combination) ${shortLabel}`;
    //     ruleCategories["Combination"].push(entry);
    //   }
    // });
  }

  Object.values(ruleCategories).forEach((options) =>
    options.sort((a, b) => a.label.localeCompare(b.label)),
  );

  // Two-step selection: a group dropdown narrows the rule dropdown to that
  // group's rules. When editing an existing rule (or when there's only one
  // group) the group is derived rather than picked.
  const groupNames = Object.keys(ruleCategories).filter(
    (g) => ruleCategories[g].length > 0,
  );
  const groupOfRule = groupNames.find((g) =>
    ruleCategories[g].some((opt) => opt.value === ruleType),
  );
  const effectiveGroup = groupNames.includes(selectedGroup)
    ? selectedGroup
    : (groupOfRule ?? (groupNames.length === 1 ? groupNames[0] : ""));
  const groupOptions: { [category: string]: CategoryOption[] } = {
    "": groupNames.map((g) => ({ label: g, displayLabel: g, value: g })),
  };
  const ruleOptionsForGroup: { [category: string]: CategoryOption[] } =
    effectiveGroup ? { "": ruleCategories[effectiveGroup] } : {};

  const selectedEntry = useRuleDefinitions
    ? filteredRuleDefEntries.find(([k]) => k === ruleType)?.[1]
    : undefined;
  const selectedSubtype =
    selectedEntry != null && selectedEntry["rule_subtype_id"] != null
      ? Number(selectedEntry["rule_subtype_id"])
      : null;

  // Rules that apply to a user-chosen subset of outcomes (e.g. MLS
  // halftime/fulltime) get a multi-select. The choice lives outside ruleDetails
  // because the generic subtype forms replace ruleDetails wholesale on change;
  // it's merged in when the user continues.
  const activeOptionSelector =
    selectedEntry != null
      ? findRuleOptionSelector(
          `${categoryByKey[ruleType] ?? ""} ${(selectedEntry["description"] as string) ?? ""}`,
        )
      : undefined;
  const selectedOptions: string[] | undefined = activeOptionSelector
    ? (optionSelections[activeOptionSelector.detailKey] ??
      activeOptionSelector.options.map((o) => o.value)) // default: every outcome
    : undefined;

  // Explanatory blurb for rule types whose parameter form (a plain
  // threshold) isn't self-explanatory on its own — currently Arbitrage/+EV.
  const activeDescriptionEntry =
    selectedEntry != null
      ? findRuleDescription(
          `${categoryByKey[ruleType] ?? ""} ${(selectedEntry["description"] as string) ?? ""}`,
        )
      : undefined;

  // Spread/Moneyline-style rules get signed steppers (see ruleLineType.ts).
  const lineType: HitsValueLineType | undefined =
    selectedEntry != null
      ? getLineType(
          `${categoryByKey[ruleType] ?? ""} ${(selectedEntry["description"] as string) ?? ""}`,
        )
      : undefined;

  const isYesNoRule = selectedEntry?.["yes_no"] === true;

  const handleRuleDetailChange = (details: RuleDetails) => {
    setRuleDetails(details);
  };

  const handlePrevious = (): void => {
    setFormState(formState - 1);
  };

  const handleNext = (): void => {
    if (!showDetails) {
      setValidationError({
        title: "Rule Required",
        message: "A completed rule is required to continue.",
      });
      return;
    }

    // if (selectedSubtype != null) {
    //   const config = SUBTYPE_CONFIG[selectedSubtype];
    //   if (config) {
    //     for (const field of config.fields) {
    //       const val = (ruleDetails as Record<string, unknown>)?.[field.key];
    //       if (field.type === "direction" && !val) {
    //         setValidationError({
    //           title: "Direction Required",
    //           message: "Please select a direction (Up or Down).",
    //         });
    //         return;
    //       }
    //       if (
    //         field.type === "number" &&
    //         (val === undefined || val === null || val === "")
    //       ) {
    //         setValidationError({
    //           title: "Value Required",
    //           message: `Please enter a value for "${field.label}".`,
    //         });
    //         return;
    //       }
    //     }
    //   }
    // }

    if (activeOptionSelector && selectedOptions) {
      if (selectedOptions.length === 0) {
        setValidationError({
          title: "Selection Required",
          message: "Please select at least one option for this rule.",
        });
        return;
      }
    }

    innerRule["details"] = {
      ...(ruleDetails as RuleDetails),
      ...(activeOptionSelector && selectedOptions
        ? { [activeOptionSelector.detailKey]: selectedOptions }
        : {}),
      ...(isYesNoRule ? { side: yesNoSide } : {}),
    };
    innerRule["type"] = ruleType;

    setRule(innerRule);
    setFormState(formState + 1);
  };

  const updateRuleGroup = (group: string) => {
    if (group === effectiveGroup) return;
    setSelectedGroup(group);
    // The chosen rule belongs to the previous group — clear it.
    if (groupOfRule !== group) {
      setRuleType("Select an option");
      setRuleDetails({});
      setShowDetails(false);
    }
  };

  const updateRuleValue = (value: string) => {
    if (value !== ruleType) {
      if (useRuleDefinitions) {
        const entry = filteredRuleDefEntries.find(([k]) => k === value)?.[1];
        const subtype =
          entry?.["rule_subtype_id"] != null
            ? Number(entry["rule_subtype_id"])
            : null;
        if (subtype != null) {
          setRuleDetails({});
        } else {
          setRuleDetails((entry?.["defaults"] as RuleDetails) ?? {});
        }
        // } else {
        //   setRuleDetails(ruleOptions[value].defaults);
      }
    }
    setShowDetails(true);
    setRuleType(value);
  };

  return (
    <div style={{ marginTop: 10, padding: "0 10px" }}>
      <div
        className=" border_radius"
        style={
          {
            // padding: "0 10px",
            // backgroundColor: "#181818",
            // borderRadius: 8,
          }
        }
      >
        <div className="row center_me">
          <div
            className=" border_radius"
            style={{
              // backgroundColor: "#2a2a2a",
              // borderRadius: 8,
              marginTop: 10,
              paddingLeft: 15,
              paddingRight: 15,
            }}
          >
            <h2
              className="font_family_base "
              style={{
                fontSize: 16,
                fontWeight: "bold",
                marginBottom: 10,
                textAlign: "center",
                marginTop: 10,
                // color: "#fff",
              }}
            >
              Need another rule defined? Please use the "Contact Us" page and we
              will be happy to add it.
            </h2>
          </div>
        </div>
        <div className="row center_me">
          <div
            className=" border_radius"
            style={{
              // backgroundColor: "#2a2a2a",
              // borderRadius: 8,
              marginTop: 10,
              paddingLeft: 15,
              paddingRight: 15,
            }}
          >
            <h2
              className="font_family_base text_green"
              style={{
                fontSize: 20,
                fontWeight: "bold",
                marginBottom: 10,
                textAlign: "center",
                marginTop: 10,
                // color: "#fff",
              }}
            >
              Rule Parameters
            </h2>
          </div>
        </div>
        <div className="row center_me">
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginTop: 10,
              marginBottom: 10,
              width: "100%",
              maxWidth: 420,
              padding: "0 10px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {groupNames.length > 1 && (
                <CustomRuleDropdown
                  value={effectiveGroup}
                  onChange={updateRuleGroup}
                  categories={groupOptions}
                  placeholder="Select Rule Group"
                />
              )}
              <CustomRuleDropdown
                value={ruleType}
                onChange={updateRuleValue}
                categories={ruleOptionsForGroup}
                placeholder={
                  effectiveGroup ? "Select Rule Type" : "Select a Rule Group first"
                }
                disabled={!effectiveGroup}
              />
              {hasLockedRules && (
                <p
                  className="font_family_base"
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: "#9ca3af",
                    textAlign: "center",
                  }}
                >
                  Some rules require a Pro membership.{" "}
                  <a href="/subscription/update" style={{ color: "#4a9eff" }}>
                    Upgrade
                  </a>
                </p>
              )}
            </div>
            {showDetails && (
              <button
                onClick={() => setShowExample(true)}
                title="See an example"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "2px solid #4a9eff",
                  background: "none",
                  color: "#4a9eff",
                  fontSize: 16,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  // Line up with the rule dropdown (second row) rather than the group dropdown.
                  marginTop: groupNames.length > 1 ? 54 : 0,
                }}
              >
                i
              </button>
            )}
          </div>
        </div>
        {/* Rule sentence area: up to 800px, but never more than 80% of the
            screen. CreateRule widens the page to 820px on this step so the
            full 800px fits inside this form's 10px side padding. */}
        <div
          style={{
            width: "min(800px, 80vw)",
            maxWidth: "100%",
            margin: "0 auto",
          }}
        >
          {showDetails && isYesNoRule && (
            <div
              className="center_me"
              style={{ gap: 10, marginTop: 10, flexWrap: "wrap" }}
            >
              <span className="font_family_base" style={{ fontSize: 16 }}>
                Watch the price for:
              </span>
              <div
                className="background_1 border_radius"
                style={{ width: 180, position: "relative", display: "flex" }}
              >
                <div
                  className="background_3 border_radius toggle-slider"
                  style={{
                    position: "absolute",
                    width: "50%",
                    height: "100%",
                    top: 0,
                    border: "2px solid #007bff",
                    boxSizing: "border-box",
                    transform:
                      yesNoSide === "yes" ? "translateX(0%)" : "translateX(100%)",
                    transition: "transform 0.2s",
                  }}
                />
                {(["yes", "no"] as const).map((val) => (
                  <div
                    key={val}
                    className="font_family_base bill-type-button-base border_radius"
                    onClick={() => setYesNoSide(val)}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      position: "relative",
                      zIndex: 1,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {val === "yes" ? "Yes" : "No"}
                  </div>
                ))}
              </div>
            </div>
          )}
          {showDetails && activeDescriptionEntry && (
            // Same paragraph style as the "Example" modal text below, so this
            // reads as one voice with the rest of the rule form.
            <p
              className="font_family_base"
              style={{
                color: "#e0e0e0",
                fontSize: 15,
                lineHeight: 1.6,
                textAlign: "center",
                maxWidth: 420,
                margin: "10px auto 0",
                padding: "0 16px",
              }}
            >
              {activeDescriptionEntry.blurb}
            </p>
          )}
          {/* {showDetails && !useRuleDefinitions && (
            <RuleLogicComponent
              key={ruleType}
              ruleType={ruleType}
              gamePartCount={gamePartCount}
              gamePartName={gamePartName}
              ruleDetails={ruleDetails as RuleDetails}
              updateRuleDetails={handleRuleDetailChange}
            />
          )} */}
          {showDetails && useRuleDefinitions && selectedSubtype === 1 && (
            <GenericValueChange
              key={ruleType}
              ruleTargetName={
                (selectedEntry?.["description"] as string) ?? "Value"
              }
              bigValues={lineType === "moneyline"}
              gamePartName={gamePartName}
              gamePartCount={gamePartCount}
              ruleDetails={ruleDetails as Record<string, unknown>}
              updateRuleDetails={handleRuleDetailChange}
            />
          )}
          {showDetails && useRuleDefinitions && selectedSubtype === 2 && (
            <GenericPercentChange
              key={ruleType}
              ruleTargetName={
                (selectedEntry?.["description"] as string) ?? "Value"
              }
              bigValues={false}
              gamePartName={gamePartName}
              gamePartCount={gamePartCount}
              ruleDetails={ruleDetails as Record<string, unknown>}
              updateRuleDetails={handleRuleDetailChange}
            />
          )}
          {showDetails && useRuleDefinitions && selectedSubtype === 3 && (
            <GenericHitsValue
              key={ruleType}
              ruleTargetName={
                (selectedEntry?.["description"] as string) ?? "Value"
              }
              lineType={lineType}
              bigValues={false}
              gamePartName={gamePartName}
              gamePartCount={gamePartCount}
              ruleDetails={ruleDetails as Record<string, unknown>}
              updateRuleDetails={handleRuleDetailChange}
            />
          )}
          {showDetails && useRuleDefinitions && selectedSubtype === 4 && (
            <GenericTeamValueChange
              key={ruleType}
              ruleTargetName={
                (selectedEntry?.["description"] as string) ?? "Value"
              }
              bigValues={lineType === "moneyline"}
              gamePartName={gamePartName}
              gamePartCount={gamePartCount}
              ruleDetails={ruleDetails as Record<string, unknown>}
              updateRuleDetails={handleRuleDetailChange}
            />
          )}
          {showDetails && useRuleDefinitions && selectedSubtype === 5 && (
            <GenericTeamPercentChange
              key={ruleType}
              ruleTargetName={
                (selectedEntry?.["description"] as string) ?? "Value"
              }
              bigValues={false}
              gamePartName={gamePartName}
              gamePartCount={gamePartCount}
              ruleDetails={ruleDetails as Record<string, unknown>}
              updateRuleDetails={handleRuleDetailChange}
            />
          )}
          {showDetails && useRuleDefinitions && selectedSubtype === 6 && (
            <GenericTeamHitsValue
              key={ruleType}
              ruleTargetName={
                (selectedEntry?.["description"] as string) ?? "Value"
              }
              lineType={lineType}
              bigValues={false}
              gamePartName={gamePartName}
              gamePartCount={gamePartCount}
              ruleDetails={ruleDetails as Record<string, unknown>}
              updateRuleDetails={handleRuleDetailChange}
            />
          )}
          {showDetails && activeOptionSelector && selectedOptions && (
            <RuleOptionMultiSelect
              selector={activeOptionSelector}
              selected={selectedOptions}
              onChange={(next) =>
                setOptionSelections((prev) => ({
                  ...prev,
                  [activeOptionSelector.detailKey]: next,
                }))
              }
            />
          )}
          {/* {showDetails &&
            useRuleDefinitions &&
            selectedSubtype != null &&
            selectedSubtype > 3 && (
              <SubtypeParamsForm
                subtype={selectedSubtype}
                details={(ruleDetails as Record<string, unknown>) ?? {}}
                onChange={handleRuleDetailChange}
              />
            )} */}
          {/* Rule definitions with no rule_subtype_id (currently Arb/+EV)
              don't get one of the Generic* components above — give them the
              same "Hits Value" threshold input (equality + value). */}
          {showDetails &&
            useRuleDefinitions &&
            selectedSubtype === null &&
            activeDescriptionEntry && (
              <GenericHitsValue
                key={ruleType}
                ruleTargetName={
                  activeDescriptionEntry.ruleTargetName ??
                  (selectedEntry?.["description"] as string) ??
                  "Value"
                }
                fixedComparison={activeDescriptionEntry.fixedComparison}
                showValuePercent={activeDescriptionEntry.valueSuffix === "%"}
                bigValues={false}
                gamePartName={gamePartName}
                gamePartCount={gamePartCount}
                ruleDetails={ruleDetails as Record<string, unknown>}
                updateRuleDetails={handleRuleDetailChange}
              />
            )}
          {showDetails &&
            useRuleDefinitions &&
            selectedSubtype === null &&
            !activeDescriptionEntry &&
            selectedEntry && (
              <div
                style={{
                  padding: "12px 16px",
                  margin: "8px auto 0",
                  textAlign: "center",
                }}
              >
                {/* <p
                  className="font_family_base"
                  style={{
                    color: "#6b7280",
                    fontSize: 14,
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                  }}
                > */}
                <RuleLogicComponent
                  key={ruleType}
                  ruleType={ruleType}
                  gamePartCount={gamePartCount}
                  gamePartName={gamePartName}
                  ruleDetails={ruleDetails as RuleDetails}
                  updateRuleDetails={handleRuleDetailChange}
                />
                {/* {(selectedEntry["example"] as string) ||
                    (selectedEntry["description"] as string) ||
                    ""} */}
                {/* </p> */}
              </div>
            )}
        </div>
      </div>
      <div
        className="center_me"
        style={{
          display: "flex",
          flexDirection: "row",
          //          justifyContent: "space-around",
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: 20,
          paddingBottom: 20,
        }}
      >
        <button
          className="my-form-button button-base button-3d font_family_base"
          style={{
            marginTop: "5px",
            marginBottom: "5px",
            backgroundImage: `url(${buttonBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "white",
            fontWeight: 700,
          }}
          onClick={handlePrevious}
        >
          ← Previous
        </button>
        <button
          className="my-form-button button-base button-3d font_family_base"
          style={{
            marginTop: "5px",
            marginBottom: "5px",
            backgroundImage: `url(${buttonBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "white",
            fontWeight: 700,
          }}
          onClick={handleNext}
        >
          Next →
        </button>
      </div>

      {showExample && (
        <div
          onClick={() => setShowExample(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="background_2 border_radius"
            style={{
              padding: 30,
              maxWidth: 440,
              width: "90%",
              border: "1px solid #333",
            }}
          >
            <p
              className="font_family_base"
              style={{
                color: "#4a9eff",
                fontSize: 13,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 12,
                textAlign: "center",
              }}
            >
              Example
            </p>
            <p
              className="font_family_base"
              style={{
                color: "#e0e0e0",
                fontSize: 15,
                lineHeight: 1.6,
                whiteSpace: "pre-line",
                marginBottom: 20,
              }}
            >
              {((selectedEntry?.["example"] as string) ?? "").replace(
                /\\n/g,
                "\n",
              )}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                onClick={() => setShowExample(false)}
                className="my-form-button button-base button-3d font_family_base"
                style={{
                  backgroundImage: `url(${buttonBackground})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Error Modal */}
      {validationError && (
        <div
          onClick={() => setValidationError(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="background_2 border_radius"
            style={{
              padding: 30,
              maxWidth: 400,
              width: "90%",
              textAlign: "center",
              border: "1px solid #333",
            }}
          >
            <h3
              className="font_family_base"
              style={{
                color: "#fff",
                marginBottom: 15,
                fontSize: 20,
              }}
            >
              {validationError.title}
            </h3>
            <p
              className="font_family_base"
              style={{
                color: "#ccc",
                marginBottom: 25,
                fontSize: 16,
                lineHeight: 1.5,
              }}
            >
              {validationError.message}
            </p>
            <button
              onClick={() => setValidationError(null)}
              className="my-form-button-wide button-base button-3d font_family_base"
              style={{
                backgroundImage: `url(${buttonBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: "white",
                fontWeight: 700,
                padding: "12px 24px",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatcherFormRule;
